import React, { type ReactNode, useCallback } from "react";

import classNames from "classnames";

import { Button } from "../Button/Button";

import style from "./SelectButton.module.css";

interface SelectButtonCheckboxProps {
  type: "checkbox";
  value: string[];
  onChange?: (value: string[]) => void;
  size?: "small" | "medium";
  children: ReactNode;
  disabled?: boolean;
  className?: string;
}

interface SelectButtonRadioProps {
  type: "radio";
  value: string;
  onChange?: (value: string) => void;
  size?: "small" | "medium";
  children: ReactNode;
  disabled?: boolean;
  className?: string;
}

export type SelectButtonProps = SelectButtonCheckboxProps | SelectButtonRadioProps;

type SelectButtonComponent<T> = React.FC<T> & {
  [key in keyof SelectButtonChildren]: SelectButtonChildren[key];
};

type SelectButtonChildren = {
  Item: typeof SelectButtonItem;
};

interface SelectButtonItemProps {
  title: string;
  subtitle?: ReactNode;
  value?: string;
  selected?: boolean;
  size?: "small" | "medium";
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
}

const SelectButtonItem: React.FC<SelectButtonItemProps> = ({
  title,
  subtitle,
  selected,
  size,
  disabled,
  onClick,
  className,
}) => {
  const handleOnClick = useCallback(() => {
    if (!disabled) {
      onClick?.();
    }
  }, [disabled, onClick]);

  if (size === "small") {
    return (
      <Button
        variant="grayscale"
        className={classNames(selected && style.selected, style.button)}
        disabled={disabled}
        onClick={handleOnClick}
      >
        {title}
      </Button>
    );
  }

  return (
    <div
      className={classNames(
        style.item,
        selected && style.selected,
        disabled && style.disabled,
        className
      )}
      onClick={handleOnClick}
      role="button"
    >
      <div className={style.itemContent}>
        <div className={style.title}>{title}</div>
        {subtitle && <div className={style.subtitle}>{subtitle}</div>}
      </div>
    </div>
  );
};

export const SelectButton: SelectButtonComponent<SelectButtonProps> = ({
  type,
  value,
  onChange,
  size,
  children,
  disabled,
  className,
}) => {
  const onSelectItem = useCallback(
    (val: string) => {
      if (type === "checkbox") {
        if (value?.includes(val)) {
          onChange?.(value?.filter((v) => v !== val));
        } else {
          onChange?.([...(value || []), val]);
        }
      } else {
        onChange?.(val);
      }
    },
    [onChange, value, type]
  );

  const handleItemClicked = useCallback(
    (val: string) => {
      if (!disabled) {
        onSelectItem(val);
      }
    },
    [disabled, onSelectItem]
  );

  return (
    <div
      className={classNames(
        style.wrapper,
        disabled && style.disabled,
        size === "small" && style.small,
        className
      )}
    >
      {React.Children.map(children, (child) => {
        if (child && React.isValidElement<SelectButtonItemProps>(child)) {
          const itemValue = child.props.value;
          return React.cloneElement(child, {
            size: child.props.size ?? size,
            selected: itemValue ? value?.includes(itemValue) : false,
            disabled: child.props.disabled ?? disabled,
            onClick: () => itemValue && handleItemClicked(itemValue),
          });
        }
        return child;
      })}
    </div>
  );
};
SelectButton.Item = SelectButtonItem;

SelectButtonItem.displayName = "SelectButtonItem";
